<template>
  <div class="manual-adjustment-form">
    <franchise-select
      v-model="formData.franquia_id"
      elem-id="adjust-franquia-id"
      data-testid="adjust-franquia-id"
      :label="t('Franquia')"
    />
    <input-number
      v-model="formData.credits"
      elem-id="adjust-credits"
      data-testid="adjust-credits"
      :label="t('Créditos (use negativo para debitar)')"
      :maxFractionDigits="2"
    />
    <input-text
      v-model="formData.reason"
      elem-id="adjust-reason"
      data-testid="adjust-reason"
      :label="t('Motivo (obrigatório)')"
    />

    <!-- Projected balance -->
    <div
      v-if="formData.franquia_id && projectedBalanceMicro !== null"
      class="projected-balance"
      data-testid="adjust-projected-balance"
    >
      {{ t('Saldo projetado') }}:
      <strong>{{ formattedProjectedBalance }}</strong>
    </div>

    <!-- Negative-balance warning + confirm checkbox -->
    <div
      v-if="showNegativeWarning"
      class="negative-warning"
      data-testid="adjust-negative-warning"
    >
      <span class="warning-icon">⚠</span>
      {{ t('Atenção: este ajuste resultará em saldo negativo.') }}
      <label class="confirm-label" data-testid="adjust-negative-confirm-label">
        <input
          v-model="negativeConfirmed"
          type="checkbox"
          data-testid="adjust-negative-confirm"
        />
        {{ t('Confirmo que desejo prosseguir com saldo negativo') }}
      </label>
    </div>

    <div v-if="errors.franquia_id" class="error-message" data-testid="adjust-error-franquia">
      {{ errors.franquia_id }}
    </div>
    <div v-if="errors.credits" class="error-message" data-testid="adjust-error-credits">
      {{ errors.credits }}
    </div>
    <div v-if="errors.reason" class="error-message" data-testid="adjust-error-reason">
      {{ errors.reason }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { emitValidationFailed } from '../../../composables/useFormValidation';
import { eventBus } from '../../../eventBus';
import { useCreditWallet, MICRO_PER_CREDIT } from '../../../composables/useCreditWallet';
import { formatCredits } from '../../assistant/formatCredits';
import { log } from '../../../utils/DebugLogger';
import type { AiCredits_AdjustInput } from '../../../contracts/AiCredits_AdjustInput';

const { t } = useI18n();
const { adjust, fetchWallet, balanceMicro, selectedFranquiaId } = useCreditWallet();

const emit = defineEmits<{
  'submit-success': [];
}>();

const formData = reactive({
  franquia_id: null as number | string | null,
  credits: null as number | null,
  reason: '',
});

const errors = reactive({
  franquia_id: '',
  credits: '',
  reason: '',
});

const negativeConfirmed = ref(false);

/** Signed micro delta based on credits input. */
const signedMicro = computed<number | null>(() => {
  if (formData.credits === null || formData.credits === undefined) return null;
  return Math.round(formData.credits * MICRO_PER_CREDIT);
});

/** Projected balance after the adjustment (micro). Only when wallet is loaded for this franchise. */
const projectedBalanceMicro = computed<number | null>(() => {
  if (signedMicro.value === null) return null;
  const fid = formData.franquia_id ? Number(formData.franquia_id) : null;
  if (fid === null || fid !== selectedFranquiaId.value) return null;
  return balanceMicro.value + signedMicro.value;
});

const formattedProjectedBalance = computed<string>(() => {
  if (projectedBalanceMicro.value === null) return '';
  return `${formatCredits(projectedBalanceMicro.value)} cr`;
});

const showNegativeWarning = computed<boolean>(() => {
  return projectedBalanceMicro.value !== null && projectedBalanceMicro.value < 0;
});

// Fetch wallet for the selected franchise so we can show projected balance.
watch(
  () => formData.franquia_id,
  async (val) => {
    if (val) {
      try {
        await fetchWallet(Number(val));
      } catch {
        // Non-fatal: projected balance simply won't appear if wallet fetch fails
      }
    }
  },
);

function validateForm(): boolean {
  errors.franquia_id = '';
  errors.credits = '';
  errors.reason = '';

  let valid = true;

  if (!formData.franquia_id) {
    errors.franquia_id = t('Selecione uma franquia');
    valid = false;
  }

  if (formData.credits === null || formData.credits === undefined || formData.credits === 0) {
    errors.credits = t('Informe a quantidade de créditos (diferente de zero)');
    valid = false;
  }

  if (!formData.reason || !formData.reason.trim()) {
    errors.reason = t('O motivo é obrigatório');
    valid = false;
  }

  if (!valid) {
    emitValidationFailed('ManualAdjustmentForm', { ...errors });
  }

  return valid;
}

async function handleSubmit(): Promise<void> {
  log('ManualAdjustmentForm', 'handleSubmit', 'Validating form');

  if (!validateForm()) {
    return;
  }

  // Block submission when projection is negative and the user has not confirmed.
  if (showNegativeWarning.value && !negativeConfirmed.value) {
    emitValidationFailed('ManualAdjustmentForm', {
      negativeBalance: t('Confirme o ajuste com saldo negativo antes de prosseguir'),
    });
    return;
  }

  const body: AiCredits_AdjustInput = {
    franquia_id: Number(formData.franquia_id),
    signed_micro: signedMicro.value as number,
    reason: formData.reason.trim(),
    request_id: crypto.randomUUID(),
  };

  log('ManualAdjustmentForm', 'handleSubmit', 'Submitting adjustment', body);
  try {
    await adjust(body);
  } catch (err) {
    // A recusa do backend chega em HTTP 200 (envelope success:false) e agora
    // rejeita no httpService (throwOnEnvelopeFailure). Sem este catch a
    // rejeição não teria dono: o modal ficaria aberto e mudo.
    log('ManualAdjustmentForm', 'handleSubmit', 'Erro ao ajustar saldo', {
      message: (err as Error)?.message,
    });
    eventBus.emit('globalMessage.error', t('Erro ao ajustar o saldo de créditos'));
    return;
  }

  // Reset form
  formData.franquia_id = null;
  formData.credits = null;
  formData.reason = '';
  negativeConfirmed.value = false;

  emit('submit-success');
}

defineExpose({
  handleSubmit,
  formData,
  negativeConfirmed,
});
</script>

<style scoped>
.manual-adjustment-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.projected-balance {
  font-size: 0.9rem;
  color: #555;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 4px;
}

.negative-warning {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  background: #fff8e1;
  border: 1px solid #f59e0b;
  border-radius: 4px;
  font-size: 0.9rem;
  color: #92400e;
}

.warning-icon {
  margin-right: 4px;
}

.confirm-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.875rem;
}

.error-message {
  color: var(--p-red-600, #dc2626);
  font-size: 0.875rem;
}
</style>
